import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { type EventData, globalObservability } from '@opendoor/observability/slim';

import {
  type AuthErrorProvider,
  getAuthErrorMessage,
  hasAuthErrorProviderParam,
  normalizeAuthErrorCode,
  type OAuthRedirectErrorCode,
  readAuthErrorProviderFromLocation,
  readRawAuthErrorFromLocation,
  stripAuthErrorFromUrl,
} from './authError';
import { sanitizeRedirectPath } from './redirectPath';

type RedirectInitiatedEvent = EventData<'authentication', 'redirectInitiated'>;
const WEB_AUTH_SOURCE: RedirectInitiatedEvent['authSource'] = 0;
const WEB_PLATFORM: RedirectInitiatedEvent['platform'] = 1;

interface RedirectAuthGroupState {
  /**
   * True inside a RedirectAuthGroup. The group owns the fallback
   * (absent/unknown-provider) error in a slot below all buttons, so a button in
   * a group must not also render it inline.
   */
  inGroup: boolean;
  /** The group's `publishError`, reaching the buttons inside it. */
  publishError?: PublishAuthError;
}

/** Hands an error's user-facing copy to a surface that renders it elsewhere. */
export type PublishAuthError = (message: string) => void;

// Default: no group → standalone buttons keep the legacy inline Google-fallback
// behavior. Module-level so the identity is stable.
const NO_GROUP: RedirectAuthGroupState = { inGroup: false };

const RedirectAuthGroupContext = createContext<RedirectAuthGroupState>(NO_GROUP);

export type RedirectIntent = 'reauth';

type PublicButtonSize = NonNullable<RedirectAuthButtonProps['size']>;

// The public `size` API predates atoms (xsmall|small|medium|large); atoms
// Button only has sm|md|lg. Map at the boundary so call sites don't change.
const ATOMS_SIZE: Record<PublicButtonSize, 'sm' | 'md' | 'lg'> = {
  xsmall: 'sm',
  small: 'sm',
  medium: 'md',
  large: 'lg',
};

/**
 * Per-provider configuration for a redirect-based OAuth button. Each provider
 * (Google, Apple, …) supplies one of these; everything else is shared.
 */
export interface RedirectAuthProvider {
  /** Server-side OAuth entry path, e.g. `/auth/google`. */
  authPath: string;
  /** Journey-event auth method emitted for this provider. */
  authMethod: RedirectInitiatedEvent['authMethod'];
  /** Display name interpolated into provider-specific error copy, e.g. `Google`. */
  providerName: string;
  /** Default button label, e.g. `Continue with Google`. */
  defaultLabel: string;
  /**
   * Stable id stem for analyticsName / testID / telemetry labels, e.g. `google`.
   * Also the inline-error attribution key; typed as AuthErrorProvider so adding a
   * provider forces a matching AUTH_ERROR_PROVIDERS entry at compile time.
   */
  idPrefix: AuthErrorProvider;
  /**
   * Brand icon rendered inside the button, before the label. Typed as a plain
   * call signature (not React.ComponentType) so both plain function components
   * and forwardRef components satisfy it directly.
   */
  Icon: (props: { size?: number; 'aria-hidden'?: boolean }) => React.ReactNode;
  /**
   * Optional extra classes merged onto the atoms Button. Used to match
   * provider-specific brand styling that the shared variants don't cover (e.g.
   * Apple's solid near-black background from the design tokens).
   */
  buttonClassName?: string;
}

export interface RedirectAuthButtonProps {
  analyticsPrefix: string;
  /** Optional post-login destination path. Passed as ?redirect_to= query param. */
  redirectTo?: string;
  /**
   * Optional server-side intent. When set to `'reauth'`, the button forwards
   * `?redirect_intent=reauth` to the provider auth path so Rails treats the
   * round-trip as a session reauth (must match `current_user`) rather than a
   * fresh login. When omitted and no `redirectTo` is given, reauth callers
   * default `redirect_to` to the current page URL so the user lands back where
   * they were.
   */
  redirectIntent?: RedirectIntent;
  /** Button label text. Defaults to the provider's `defaultLabel`. */
  label?: string;
  /** Button size. Defaults to "large" */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /** Button variant. Defaults to "secondary" */
  variant?: 'primary' | 'secondary' | 'secondaryInverse';
  /**
   * When true, the button stretches to fill its container with no maxWidth
   * cap. Defaults to false (content-sized button in a 400px-max wrapper),
   * matching existing redirect-button call sites.
   */
  fullWidth?: boolean;
  /** Optional callback fired before navigation. Awaited if it returns a promise. */
  onBeforeNavigate?: () => void | Promise<void>;
  /** Optional error handler if onBeforeNavigate throws. Receives the caught error. Throw from this handler to block navigation. Awaited if it returns a promise. */
  onBeforeNavigateError?: (error: unknown) => void | Promise<void>;
}

/**
 * Redirect-based OAuth button for the server-side flow. Navigates to the
 * provider's auth path — Rails handles the full OAuth exchange via OmniAuth.
 * No provider JavaScript SDK in the login path. Concrete buttons
 * (GoogleAuthRedirectButton, AppleAuthRedirectButton) are thin wrappers that
 * supply a `RedirectAuthProvider`.
 */
export const RedirectAuthButton: React.FC<
  RedirectAuthButtonProps & { provider: RedirectAuthProvider }
> = ({
  provider,
  analyticsPrefix,
  redirectTo,
  redirectIntent,
  label = provider.defaultLabel,
  size = 'large',
  variant = 'secondary',
  fullWidth = false,
  onBeforeNavigate,
  onBeforeNavigateError,
}) => {
  const handlePress = useRedirectHandler({
    provider,
    redirectTo,
    redirectIntent,
    onBeforeNavigate,
    onBeforeNavigateError,
  });
  // Inside a RedirectAuthGroup the group renders fallback errors below the
  // buttons, so this button handles only its own attributed error. Standalone,
  // the fallback provider (Google) keeps rendering fallback errors inline.
  const { inGroup, publishError } = useContext(RedirectAuthGroupContext);
  const ownsFallback = !inGroup && provider.idPrefix === FALLBACK_PROVIDER;
  const authErrorCode = useAuthErrorOnMount(provider, ownsFallback);
  // A surface with a shared alert region takes the copy instead: this button's
  // own error belongs in that one region, not under the button.
  usePublishedAuthError(
    publishError && authErrorCode
      ? getAuthErrorMessage(authErrorCode, provider.providerName)
      : null,
    publishError,
  );
  const iconSize = size === 'xsmall' || size === 'small' ? 20 : 24;
  const { Icon } = provider;

  return (
    <Col className={fullWidth ? 'w-full' : 'w-full max-w-[25rem]'}>
      <Button
        aria-label={label}
        analyticsName={`${analyticsPrefix}-${provider.idPrefix}-redirect-button`}
        data-testid={`${provider.idPrefix}-auth-redirect-button`}
        size={ATOMS_SIZE[size]}
        variant={variant}
        onClick={handlePress}
        className={cn('w-full', provider.buttonClassName)}
      >
        <Icon size={iconSize} aria-hidden />
        {label}
      </Button>
      {authErrorCode && !publishError ? (
        <AuthErrorMessage
          code={authErrorCode}
          providerName={provider.providerName}
          testId={`${provider.idPrefix}-auth-error-message`}
        />
      ) : null}
    </Col>
  );
};

/** Inline auth-error text with the alert a11y roles. Shared by the per-provider
 *  button and the group's fallback slot. */
const AuthErrorMessage: React.FC<{
  code: OAuthRedirectErrorCode;
  providerName?: string;
  testId: string;
}> = ({ code, providerName, testId }) => (
  <Box className="pt-1" role="alert" aria-live="polite" data-testid={testId}>
    <Text as="span" className="text-body-sm text-od-content-error">
      {getAuthErrorMessage(code, providerName)}
    </Text>
  </Box>
);

/**
 * Wraps a set of redirect-auth buttons and owns the fallback (absent/unknown
 * `auth_error_provider`) error, rendering it once below all the buttons rather
 * than under whichever button happens to be the fallback. Provider-attributed
 * errors still render inline under their own button. Without this wrapper,
 * standalone buttons keep the legacy inline Google-fallback behavior.
 *
 * Renders a Fragment, not a layout box, so it imposes no spacing of its own —
 * the fallback error becomes the last child of the surface's existing container
 * (inheriting its gap) and the buttons keep their original layout. This lets it
 * drop into surfaces built on any design system (atoms, bricks-next, …).
 *
 * `publishError` inverts that: a surface that already owns a single alert region
 * takes the copy and renders it there, for both the fallback error and the
 * buttons' own attributed ones, so a redirect failure doesn't open a second
 * error zone below the stack. Omitted keeps the inline rendering, which is what
 * a surface without such a region needs.
 */
export const RedirectAuthGroup: React.FC<{
  children: React.ReactNode;
  publishError?: PublishAuthError;
}> = ({ children, publishError }) => {
  const fallbackErrorCode = useFallbackAuthErrorOnMount();
  usePublishedAuthError(
    publishError && fallbackErrorCode ? getAuthErrorMessage(fallbackErrorCode) : null,
    publishError,
  );
  const groupState = useMemo<RedirectAuthGroupState>(
    () => ({ inGroup: true, publishError }),
    [publishError],
  );
  return (
    <RedirectAuthGroupContext.Provider value={groupState}>
      {children}
      {fallbackErrorCode && !publishError ? (
        <AuthErrorMessage code={fallbackErrorCode} testId="fallback-auth-error-message" />
      ) : null}
    </RedirectAuthGroupContext.Provider>
  );
};

/**
 * Hands `message` to the surface's shared alert region once it resolves. The
 * error is read from the URL in a mount effect, so it arrives a render after
 * mount — publishing has to follow it rather than happening during render.
 */
function usePublishedAuthError(message: string | null, publishError?: PublishAuthError): void {
  useEffect(() => {
    if (message && publishError) {
      publishError(message);
    }
  }, [message, publishError]);
}

function navigateToAuth(authPath: string, redirectTo?: string, redirectIntent?: RedirectIntent) {
  const url = new URL(authPath, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);

  const redirectFromParams = currentParams.get('redirect_to');
  currentParams.delete('redirect_to');

  currentParams.forEach((value, key) => {
    url.searchParams.set(key, value);
  });

  // Build the reauth fallback from the redirect_to-stripped param set so an
  // inbound `?redirect_to=...` can't smuggle a nested target into the value
  // we hand Rails. sanitizeRedirectPath only inspects the path prefix, so a
  // raw window.location.search would let an unsafe inner `redirect_to=` ride
  // through into session['original_url'].
  const preservedSearch = currentParams.toString();
  const reauthDefaultRedirect =
    redirectIntent === 'reauth'
      ? `${window.location.pathname}${preservedSearch ? `?${preservedSearch}` : ''}`
      : undefined;

  // Reauth must always resolve to the current-page default when an explicit
  // `redirectTo` fails sanitization — never to inbound `redirect_to` (stripped
  // above for reauth) or `undefined`. Preserves the "land back where you were"
  // guarantee for any reauth caller that passes a malformed `redirectTo`.
  const safeRedirect =
    redirectIntent === 'reauth'
      ? (sanitizeRedirectPath(redirectTo) ?? sanitizeRedirectPath(reauthDefaultRedirect))
      : (sanitizeRedirectPath(redirectTo) ?? sanitizeRedirectPath(redirectFromParams));
  if (safeRedirect) {
    url.searchParams.set('redirect_to', safeRedirect);
  }

  if (redirectIntent) {
    url.searchParams.set('redirect_intent', redirectIntent);
  }

  window.location.href = url.pathname + url.search;
}

function trackRedirectInitiated(provider: RedirectAuthProvider) {
  globalObservability.trackJourneyEvent(
    'authentication',
    'redirectInitiated',
    {
      authMethod: provider.authMethod,
      authSource: WEB_AUTH_SOURCE,
      platform: WEB_PLATFORM,
    },
    undefined,
    { flush: true },
  );
}

function reportRedirectInitiatedTrackingError(provider: RedirectAuthProvider, error: unknown) {
  try {
    globalObservability.trackError(
      `${provider.idPrefix}-auth-redirect-initiated-journey-event-failed`,
      error instanceof Error ? error : new Error(String(error)),
    );
  } catch {
    // Auth navigation should continue even if error reporting is unavailable.
  }
}

function useRedirectHandler({
  provider,
  redirectTo,
  redirectIntent,
  onBeforeNavigate,
  onBeforeNavigateError,
}: Pick<
  RedirectAuthButtonProps,
  'redirectTo' | 'redirectIntent' | 'onBeforeNavigate' | 'onBeforeNavigateError'
> & { provider: RedirectAuthProvider }) {
  return useCallback(async () => {
    try {
      trackRedirectInitiated(provider);
    } catch (e) {
      reportRedirectInitiatedTrackingError(provider, e);
    }

    try {
      await onBeforeNavigate?.();
    } catch (e) {
      await onBeforeNavigateError?.(e);
    }

    // Flush buffered journey events before navigating away.
    // send() uses keepalive: true so the browser completes the request even during unload.
    globalObservability.getJourneyEventClient().send?.();

    navigateToAuth(provider.authPath, redirectTo, redirectIntent);
  }, [provider, redirectTo, redirectIntent, onBeforeNavigate, onBeforeNavigateError]);
}

// Provider that owns a fallback (absent/unknown `auth_error_provider`) error
// when there is no surrounding RedirectAuthGroup. Matches the GROWTH-1386
// single-button default. Typed so removing 'google' from AUTH_ERROR_PROVIDERS
// breaks the build. Inside a group the group owns fallback errors instead, so
// the legacy "every surface must co-mount Google" invariant no longer applies.
const FALLBACK_PROVIDER: AuthErrorProvider = 'google';

// Drift-detection tag: 'matched' = param named this provider; 'fallback' = param
// absent; 'unknown' = param present but off-allowlist (Rails drift).
type AuthErrorProviderMatch = 'matched' | 'fallback' | 'unknown';

/**
 * Reads `?auth_error=` once on mount, fires telemetry, strips the params, and
 * returns the code this button should render inline — or null if the error
 * belongs to another button, or to the surrounding group's fallback slot.
 *
 * `ownsFallback` is true only when this button must also handle the
 * fallback (absent/unknown-provider) case inline — i.e. it is the fallback
 * provider AND no RedirectAuthGroup is present to own that case below the
 * buttons. Inside a group the button handles only its own attributed error.
 */
function useAuthErrorOnMount(
  provider: RedirectAuthProvider,
  ownsFallback: boolean,
): OAuthRedirectErrorCode | null {
  const [code, setCode] = useState<OAuthRedirectErrorCode | null>(null);

  useEffect(() => {
    const raw = readRawAuthErrorFromLocation();

    // Stray auth_error_provider with no auth_error: the inline fallback owner
    // cleans it up so it doesn't survive in the URL.
    if (raw === null) {
      if (ownsFallback && hasAuthErrorProviderParam()) {
        stripAuthErrorFromUrl();
      }
      return;
    }

    const errorProvider = readAuthErrorProviderFromLocation();
    const isAttributedToThisButton = errorProvider === provider.idPrefix;
    const isFallbackForThisButton = errorProvider === null && ownsFallback;
    if (!isAttributedToThisButton && !isFallbackForThisButton) return;

    const match: AuthErrorProviderMatch = errorProvider
      ? 'matched'
      : hasAuthErrorProviderParam()
        ? 'unknown'
        : 'fallback';

    const normalized = normalizeAuthErrorCode(raw);
    setCode(normalized);
    stripAuthErrorFromUrl();
    reportAuthErrorDisplayed(provider, normalized, raw, errorProvider, match);
  }, [provider, ownsFallback]);

  return code;
}

/**
 * Group-owned counterpart to useAuthErrorOnMount: claims the fallback
 * (absent/unknown `auth_error_provider`) error, strips the params, fires
 * telemetry, and returns the code to render below the buttons. Provider-
 * attributed errors are left to the matching button. Always mounted with the
 * group, so fallback errors render regardless of which buttons are present.
 */
function useFallbackAuthErrorOnMount(): OAuthRedirectErrorCode | null {
  const [code, setCode] = useState<OAuthRedirectErrorCode | null>(null);

  useEffect(() => {
    const raw = readRawAuthErrorFromLocation();

    // Stray auth_error_provider with no auth_error: clean it from the URL.
    if (raw === null) {
      if (hasAuthErrorProviderParam()) stripAuthErrorFromUrl();
      return;
    }

    // A known provider param means a button owns this error; leave it alone.
    if (readAuthErrorProviderFromLocation() !== null) return;

    const match: AuthErrorProviderMatch = hasAuthErrorProviderParam() ? 'unknown' : 'fallback';
    const normalized = normalizeAuthErrorCode(raw);
    setCode(normalized);
    stripAuthErrorFromUrl();
    reportFallbackAuthErrorDisplayed(normalized, raw, match);
  }, []);

  return code;
}

// Strict allowlist for raw auth_error values forwarded to telemetry. Only
// short, code-shaped strings are emitted verbatim; anything else (e.g. emails
// embedded in malformed values) is dropped to prevent accidental PII leakage.
const SAFE_RAW_AUTH_ERROR_PATTERN = /^[a-z0-9_-]{1,64}$/;

// Identity for the group's fallback (no attributed provider) telemetry. Uses a
// distinct `oauth-login` label and METHOD_UNSPECIFIED (0) authMethod rather than
// borrowing Google's, so fallback errors are their own bucket in dashboards
// instead of inflating Google's metrics.
const FALLBACK_TELEMETRY = {
  authMethod: 0 as RedirectInitiatedEvent['authMethod'], // METHOD_UNSPECIFIED
  idPrefix: 'oauth-login',
};

function buildAuthErrorData(
  authMethod: RedirectInitiatedEvent['authMethod'],
  normalized: OAuthRedirectErrorCode,
  raw: string,
  errorProvider: AuthErrorProvider | null,
  match: AuthErrorProviderMatch,
): Record<string, unknown> {
  const data: Record<string, unknown> = {
    authMethod,
    platform: WEB_PLATFORM,
    oauthErrorCode: normalized,
    oauthErrorProviderMatch: match,
  };
  if (errorProvider) {
    data.oauthErrorProviderParam = errorProvider;
  }
  if (raw !== normalized) {
    const trimmedRaw = raw.trim().toLowerCase();
    if (SAFE_RAW_AUTH_ERROR_PATTERN.test(trimmedRaw)) {
      data.oauthErrorRawCode = trimmedRaw;
    }
  }
  return data;
}

function reportAuthErrorDisplayed(
  provider: RedirectAuthProvider,
  normalized: OAuthRedirectErrorCode,
  raw: string,
  errorProvider: AuthErrorProvider | null,
  match: AuthErrorProviderMatch,
): void {
  try {
    globalObservability.trackError(
      `${provider.idPrefix}-auth-redirect-error-displayed`,
      new Error(`${provider.providerName} OAuth redirect error displayed`),
      { data: buildAuthErrorData(provider.authMethod, normalized, raw, errorProvider, match) },
    );
  } catch {
    // Telemetry must not break the inline error UI.
  }
}

function reportFallbackAuthErrorDisplayed(
  normalized: OAuthRedirectErrorCode,
  raw: string,
  match: AuthErrorProviderMatch,
): void {
  try {
    globalObservability.trackError(
      `${FALLBACK_TELEMETRY.idPrefix}-auth-redirect-error-displayed`,
      new Error('OAuth redirect error displayed'),
      { data: buildAuthErrorData(FALLBACK_TELEMETRY.authMethod, normalized, raw, null, match) },
    );
  } catch {
    // Telemetry must not break the inline error UI.
  }
}
